import React, { useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent,
  DialogActions,
  Button, 
  Typography, 
  Divider, 
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  IconButton,
  Grid,
  Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useToast } from '../../context/ToastContext';
//import axios from 'axios';
import { courseService, userService } from '../../services/api';

const NominationPopup = ({ open, onClose, nominationId }) => {
  const [loading, setLoading] = useState(true);
  const [instance, setInstance] = useState(null);
  const [learners, setLearners] = useState([]);
  const [selectedLearners, setSelectedLearners] = useState([]);
  const [showLearnersList, setShowLearnersList] = useState(false);
  const { showToast } = useToast();
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && nominationId) {
      fetchNominationDetails();
    } else {
      setInstance(null);
      setLearners([]);
      setSelectedLearners([]);
      setShowLearnersList(false);
      setError('');
    }
  }, [open, nominationId]);

  const fetchNominationDetails = async () => {
    try {
      //setLoading(true);
      setError('');
      //const response = await axios.get(`/api/courses/nomination-details/${nominationId}`);
      const response = await courseService.getNominationDetails(nominationId);
      if (response.data && response.data.instance) {
        setInstance(response.data.instance);
      } else {
        setError('Invalid nomination data received');
        showToast('Invalid nomination data received', 'error');
      }
      setShowLearnersList(false);
      //setLoading(false);
    } catch (error) {
      console.error('Error fetching nomination details:', error);
      showToast('Failed to load nomination details', 'error');
      setError('Failed to load nomination details'); //setLoading(false);
    }
  };

  const fetchLearnersList  = async () => {
    if (!instance || !instance.manager_id) {
      showToast('Manager information is missing', 'error');
      setError('Manager information is missing');
      return;
    }
    try {
      //setLoading(true);
      setError('');
      const userId = instance.manager_id;
      //const response = await axios.get(`/api/users/manager-team/${userId}/${nominationId}`);
      const response = await userService.getManagerTeam(userId, nominationId);
      setLearners(response.data);
      
      // Pre-select already nominated learners
      const nominated = response.data
        .filter(learner => learner.is_nominated > 0)
        .map(learner => learner.id);
      setSelectedLearners(nominated);
      
      setShowLearnersList(true);
      //setLoading(false);
    } catch (error) {
      console.error('Error fetching learners:', error);
      showToast('Failed to load team members', 'error');
      setError('Failed to load team members');
      //setLoading(false);
    }
  };

  const handleLearnerSelection = (learnerId) => {
    // Don't allow deselecting already nominated learners
    const learner = learners.find(l => l.id === learnerId);
    if (learner && learner.is_nominated > 0) {
      return;
    }
    
    setSelectedLearners(prev => {
      if (prev.includes(learnerId)) {
        return prev.filter(id => id !== learnerId);
      } else {
        return [...prev, learnerId];
      }
    });
  };

  const submitNominations = async () => {
    try {
      //setLoading(true);
      setError('');
      await courseService.submitNominations(nominationId, selectedLearners);
      // await axios.post('/api/courses/submit-nominations', {
      //   nominationId,
      //   learnerIds: selectedLearners
      // });      
      showToast('Nominations submitted successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Error submitting nominations:', error);
      showToast('Failed to submit nominations', 'error');
      setError('Failed to submit nominations');
      //setLoading(false);
    }
  };

  const showCourseInfo = () => {
    setShowLearnersList(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Nominate Learners</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!showLearnersList ? (
          <Box id="courseInfo">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1"><strong>Course:</strong> {instance?.course_name || ''}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1"><strong>Author:</strong> {instance?.author_name || ''}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box className="seats-info">
                    <Typography variant="body1">{(instance?.seats_available || 0)} Seats Available</Typography>
                  </Box>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={fetchLearnersList}
                  >
                    {(instance?.seats_available || 0) === 0 ? 'View Nominations' : 'Nominate'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box id="learnersList">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1"><strong>Course:</strong> {instance?.course_name || ''}</Typography>
              </Grid>
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell>Learner</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Courses signed up</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {learners.map(learner => (
                        <TableRow key={learner.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedLearners.includes(learner.id)}
                              onChange={() => handleLearnerSelection(learner.id)}
                              disabled={learner.is_nominated > 0}
                            />
                          </TableCell>
                          <TableCell>{`${learner.first_name} ${learner.last_name}`}</TableCell>
                          <TableCell>{learner.email}</TableCell>
                          <TableCell>{learner.course_count || 0} Courses</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  <Box className="seats-info">
                    <Typography variant="body1">{(instance?.seats_available || 0)} Seats Available</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>           
          </Box>
        )}
      </DialogContent>
      <DialogActions>
      {showLearnersList && (
          <>
            <Button 
              onClick={showCourseInfo}
              color="secondary"
            >
              Back
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={submitNominations}
              disabled={selectedLearners.length === 0 || 
                (selectedLearners.length - learners.filter(l => l.is_nominated > 0).length) > (instance?.seats_available || 0)}
            >
              Submit Nominations
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NominationPopup;